import {
  Accordion,
  Button,
  ButtonGroup,
  ButtonMenu,
  Css,
  defaultTestId,
  Icon,
  IconButton,
  marker,
  Palette,
  Switch,
  useModal,
  useTestIds,
  useToast,
} from "@homebound/beam";
import startCase from "lodash/startCase";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { SearchBox } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import {
  DesignPackageLeftNavSlotFragment,
  DesignPackageSlotsDocument,
  useCancelPlanPackageSlotDeletionMutation,
  useCreateAlternateLocationMutation,
  useDesignPackageSlotsQuery,
} from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageSlotsLeftNav.generated";
import { AddNewDesignPackageSlotModal } from "src/routes/libraries/design-catalog/design-package-configurator/routes/new/AddNewDesignPackageSlotModal";
import { fail, foldEnum, queryResult } from "src/utils";
import { useLocalStorage } from "usehooks-ts";
import { useDesignPackageConfiguratorContext } from "../DesignPackageConfiguratorContext";
import { useConfirmDeleteAlternatesModal } from "./DesignPackageDeleteAlternatesConfirmationModal";
import { useDesignPackageRoomAlternatesDrawer } from "./DesignPackageRoomAlternatesDrawer";

export function DesignPackageSlotsLeftNav() {
  const { designPackage } = useDesignPackageConfiguratorContext();
  const query = useDesignPackageSlotsQuery({
    variables: { dpId: designPackage.id, rpavId: designPackage.version.id },
    skip: !designPackage,
  });

  /** Slots may be Syncing on the backend. Detect if that's happening, and start polling until the sync is done. */
  useEffect(() => {
    query.data?.designPackage.copyInProgress || query.data?.designPackage.cloneInProgress
      ? // 2.5 sec intervals seems like enough
        query.startPolling(2_500)
      : query.stopPolling();
  }, [query]);

  return queryResult(query, ({ designPackage }) => <DesignPackageSlotsLeftNavView dpSlots={designPackage?.slots} />);
}

type DesignPackageSlotsLeftNavViewProps = { dpSlots: DesignPackageLeftNavSlotFragment[] | undefined };

function DesignPackageSlotsLeftNavView({ dpSlots }: DesignPackageSlotsLeftNavViewProps) {
  const tid = useTestIds({});
  const [search, setSearch] = useState("");
  const [auditing, setAuditing] = useState(false);
  const filteredSlots = useAdhocFilterSlots(dpSlots ?? [], search, auditing);
  const { openRoomDrawer } = useDesignPackageRoomAlternatesDrawer();
  const openConfirmDeleteAlternatesModal = useConfirmDeleteAlternatesModal();
  const { showToast, clear: clearToast } = useToast();

  const [groupBy, setGroupBy] = useLocalStorage("design-package-configurator-left-nav-group-by", GroupBy.Location);
  const groupedDpSlots = useGroupedDesignPackageSlots(filteredSlots, groupBy);

  const { designPackage, setCtxState, selectedSlots, readOnly } = useDesignPackageConfiguratorContext();
  const [createAlternateLocation] = useCreateAlternateLocationMutation();
  const [cancelPlanPackageSlotDeletion] = useCancelPlanPackageSlotDeletionMutation();

  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);
  const { openModal } = useModal();

  function openAddSlotModal(initialLocationId?: string) {
    openModal({
      content: (
        <AddNewDesignPackageSlotModal
          designPackageId={designPackage.id}
          setCtxState={setCtxState}
          initialLocationId={initialLocationId}
        />
      ),
      size: "md",
    });
  }

  async function createAltLocation(dpSlot: DesignPackageLeftNavSlotFragment) {
    // 0 feedback to users anything is going on, so put them on the edge of their seat
    showToast({ type: "info", message: `Creating alternate location...` });
    await createAlternateLocation({
      variables: { input: { designPackageId: designPackage.id, locationId: dpSlot.slot.location.id! } },
      refetchQueries: [DesignPackageSlotsDocument],
    });
    clearToast();
    showToast({ type: "success", message: `Created alternate location.` });
  }

  // if anything is selected, scroll to it so when users hit "back" it's still in view
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.scrollIntoView({ behavior: "instant", block: "center" });
  }, []);

  return (
    <div css={Css.h100.pt2.bgWhite.fg1.df.fdc.gap3.oyh.$}>
      <div css={Css.df.fdc.gap3.px2.$}>
        <SearchBox
          placeholder="Search by type, cost code, location, name..."
          clearable
          onSearch={setSearch}
          fullWidth
          debounceDelayInMs={200}
        />
        <div css={Css.df.jcsb.aic.$}>
          <ButtonGroup
            size="md"
            buttons={[
              { text: "Room", active: groupBy === GroupBy.Location, onClick: () => setGroupBy(GroupBy.Location) },
              { text: "Cost Code", active: groupBy === GroupBy.CostCode, onClick: () => setGroupBy(GroupBy.CostCode) },
            ]}
          />
          <Switch
            label="Only Incomplete"
            selected={auditing}
            onChange={setAuditing}
            labelStyle="hidden"
            hideLabel
            tooltip="Only show slots with missing selections"
          />
        </div>
      </div>
      <div css={Css.fg1.px2.oya.$}>
        {/* No slots UX */}
        {dpSlots?.isEmpty && designerModifiedSlotsEnabled && (
          <div css={Css.df.fdc.aic.tac.jcsb.gap4.$}>
            <Button variant="secondary" label="Click to add your first slot" onClick={() => openAddSlotModal()} />
          </div>
        )}
        {/* You filtered out all slots UX */}
        {dpSlots?.nonEmpty && filteredSlots.isEmpty && (
          <div css={Css.df.fdc.aic.tac.jcsb.gap4.$}>
            <div css={Css.lg.red400.$}>Filter applied. No slots to show.</div>
            <Button
              label="Click to reset filters"
              variant="secondary"
              onClick={() => {
                setCtxState({ slotIds: [] });
                setSearch(""); // will reset search but the bar may still show text because it has its own internal state
                setAuditing(false);
              }}
            />
          </div>
        )}
        {/* Regular Slot UX */}
        {Object.entries(groupedDpSlots)
          .sortBy(([primaryGroupName]) => primaryGroupName)
          .map(([primaryGroupName, groupRecord]) => {
            const entries = Object.entries(groupRecord);
            const values = entries.map(([_, slots]) => slots).flat();
            const canRemap = values.first?.slot.location.roomType?.canDesignPackageRemap ?? false;
            const isAltLocation = !nonAltLocationRegex.test(values.first?.slot.location.nonScopeLocation?.name ?? "");
            const id = defaultTestId(primaryGroupName);
            return (
              <div key={primaryGroupName} css={Css.mb3.oya.fg1.$}>
                <div data-testid={id} css={Css.df.jcsb.aic.$}>
                  <button
                    css={Css.w100.tal.py1.mdSb.onHover.bgGray100.$}
                    onClick={() => setCtxState({ slotIds: values.map((dps) => dps.slot.id) })}
                  >
                    {primaryGroupName}
                  </button>
                  {canRemap && groupBy === GroupBy.Location && (
                    <ButtonMenu
                      {...tid.altLocationMenu}
                      trigger={{ icon: "verticalDots" }}
                      items={[
                        { label: "Create Alternate", onClick: () => createAltLocation(values.first!) },
                        { label: "Room Pairing", onClick: () => openRoomDrawer(values.first!.slot.location.id) },
                        isAltLocation
                          ? {
                              label: "Delete",
                              onClick: () => openConfirmDeleteAlternatesModal(values.first!.slot.location.id),
                            }
                          : undefined,
                      ].compact()}
                    />
                  )}
                </div>
                {entries
                  .sortBy(([subGroupName]) => subGroupName)
                  .map(([subGroupName, subgroupedDpSlots]) => (
                    <Accordion
                      data-testid={`${primaryGroupName}_${subGroupName}`.replace(/\s+/g, "_")}
                      key={subGroupName + subgroupedDpSlots.length}
                      title={
                        <div css={Css.df.aic.gap1.$}>
                          {startCase(subGroupName)}
                          {/* Display a red dot if any of the slots in the subgroup are not 100% covered */}
                          {subgroupedDpSlots.some((dps) => !dps.isFullyAssigned) && (
                            <Icon icon="circle" {...tid.incomplete_indicator} inc={1.5} color={Palette.Red500} />
                          )}
                        </div>
                      }
                      titleOnClick={() => setCtxState({ slotIds: subgroupedDpSlots.map((dps) => dps.slot.id) })}
                      defaultExpanded={
                        // If filters actually filtered anything, expand
                        dpSlots?.length !== filteredSlots.length ||
                        // if we're auding, reveal the audit (specifically the icons)
                        auditing ||
                        // for whatever slot is currently selected, expand its group
                        subgroupedDpSlots.some((dps) => selectedSlots?.map((s) => s.slot.id).includes(dps.slot.id))
                      }
                      omitPadding
                      xss={Css.tal.p1.truncate.$ as any}
                      topBorder={false}
                    >
                      {/* Normal slots */}
                      <div css={Css.df.fdc.gap1.$}>
                        {subgroupedDpSlots
                          .filter(
                            (s) =>
                              !s.pendingDeletion && !s.hasPendingPlanPackageIncomingSlots && !s.isRejectedIncomingSlot,
                          )
                          .map((dpSlot) => (
                            <SlotRow
                              key={dpSlot.slot.id}
                              dpSlot={dpSlot}
                              selected={selectedSlots?.some((dps) => dps.slot.id === dpSlot.slot.id) ?? false}
                              scrollRef={(dpSlot.slot.id === selectedSlots?.[0]?.slot.id && ref) || null}
                              onClick={() => setCtxState({ slotIds: [dpSlot.slot.id] })}
                            />
                          ))}
                        {/* Pending review slots */}
                        {subgroupedDpSlots.some((s) => s.hasPendingPlanPackageIncomingSlots) && (
                          <div css={Css.bgOrange50.py2.df.fdc.gap1.marker.$}>
                            <div {...tid.pendingReview} css={Css.xs2Sb.orange500.px1.$}>
                              Pending review
                            </div>
                            {subgroupedDpSlots
                              .filter((s) => s.hasPendingPlanPackageIncomingSlots)
                              .map((dpSlot) => (
                                <SlotRow
                                  key={dpSlot.slot.id}
                                  dpSlot={dpSlot}
                                  variant="pendingReview"
                                  selected={selectedSlots?.some((dps) => dps.slot.id === dpSlot.slot.id) ?? false}
                                  scrollRef={(dpSlot.slot.id === selectedSlots?.[0]?.slot.id && ref) || null}
                                  onClick={() => setCtxState({ slotIds: [dpSlot.slot.id] })}
                                />
                              ))}
                          </div>
                        )}
                        {/* Rejected slots */}
                        {subgroupedDpSlots.some((s) => s.isRejectedIncomingSlot) && (
                          <div css={Css.bgGray100.py2.df.fdc.gap1.marker.$}>
                            <div {...tid.rejected} css={Css.xs2Sb.gray700.px1.$}>
                              Rejected
                            </div>
                            {subgroupedDpSlots
                              .filter((s) => s.isRejectedIncomingSlot)
                              .map((dpSlot) => (
                                <SlotRow
                                  key={dpSlot.slot.id}
                                  dpSlot={dpSlot}
                                  variant="rejected"
                                  selected={selectedSlots?.some((dps) => dps.slot.id === dpSlot.slot.id) ?? false}
                                  scrollRef={(dpSlot.slot.id === selectedSlots?.[0]?.slot.id && ref) || null}
                                  onClick={() => setCtxState({ slotIds: [dpSlot.slot.id] })}
                                />
                              ))}
                          </div>
                        )}
                        {/* Pending deletion slots */}
                        {designerModifiedSlotsEnabled && subgroupedDpSlots.some((s) => s.pendingDeletion) && (
                          <div css={Css.bgRed50.py2.df.fdc.gap1.marker.$}>
                            <div {...tid.pendingDeletion} css={Css.xs2Sb.red500.px1.$}>
                              Pending deletion
                            </div>
                            {subgroupedDpSlots
                              .filter((s) => s.pendingDeletion)
                              .map((dpSlot) => (
                                <SlotRow
                                  key={dpSlot.slot.id}
                                  dpSlot={dpSlot}
                                  variant="pendingDeletion"
                                  selected={selectedSlots?.some((dps) => dps.slot.id === dpSlot.slot.id) ?? false}
                                  onClick={() => setCtxState({ slotIds: [dpSlot.slot.id] })}
                                  onActionClick={() =>
                                    cancelPlanPackageSlotDeletion({
                                      variables: { input: { designPackagePlaceholderId: dpSlot.items.first!.id } },
                                      refetchQueries: [DesignPackageSlotsDocument],
                                    })
                                  }
                                />
                              ))}
                          </div>
                        )}
                        {!readOnly && designerModifiedSlotsEnabled && (
                          <div
                            css={Css.df.aic.jcc.pyPx(4).cursorPointer.br4.onHover.bgGray100.sm.$}
                            onClick={() => openAddSlotModal(subgroupedDpSlots.first?.slot.location.id)}
                          >
                            + New {startCase(subGroupName || primaryGroupName)}
                          </div>
                        )}
                      </div>
                    </Accordion>
                  ))}
              </div>
            );
          })}
      </div>
      {!readOnly && designerModifiedSlotsEnabled && (
        <div css={Css.px2.pb2.pt1.bt.bcGray200.df.fdc.$}>
          <Button
            label={
              <div css={Css.df.aic.jcc.fg1.$}>
                <Icon icon="plus" /> Add slot
              </div>
            }
            variant="secondary"
            onClick={() => openAddSlotModal()}
          />
        </div>
      )}
    </div>
  );
}

/** Tests for `... Alt 1`, `... Alt 2`. Pulled out so it's not constantly recompiled. */
const nonAltLocationRegex = /^(?!.*Alt \d+).*$/;

enum GroupBy {
  CostCode,
  Location,
}

/**
 * Double-nests Slots into Groups and Subgroups Record -> Record -> Slots, or
 * { appliances: { kitchen: [slot1, slot2], laundry: [slot3] }, ... }
 */
function useGroupedDesignPackageSlots(
  slots: DesignPackageLeftNavSlotFragment[],
  groupBy: GroupBy,
): Record<string, Record<string, DesignPackageLeftNavSlotFragment[]>> {
  return useMemo(() => {
    const [primaryGroup, subGroup] = foldEnum<
      GroupBy,
      [
        primaryGroup: (dpSlot: DesignPackageLeftNavSlotFragment) => PropertyKey,
        subGroup: (dpSlot: DesignPackageLeftNavSlotFragment) => PropertyKey,
      ]
    >(groupBy, {
      [GroupBy.CostCode]: [
        // Group by Cost Code
        (dpSlot) => dpSlot.slot.item.costCode?.name ?? dpSlot.slot.item.name ?? fail("No cost code"),
        // Subgroup by Location (prefer Nook, then Room, then ID--should move to the backend DesignPackageSlot identity)
        (dpSlot) => dpSlot.slot.location.displayLocationPath,
      ],
      [GroupBy.Location]: [
        // Group by Location (prefer Nook, then Room, then ID--should move to the backend DesignPackageSlot identity)
        (dpSlot) => dpSlot.slot.location.displayLocationPath,
        // Subgroup by Cost Code
        (dpSlot) => dpSlot.slot.item.costCode?.name ?? dpSlot.slot.item.name ?? fail("No cost code"),
      ],
    });

    return Object.entries(slots.groupBy(primaryGroup)).mapToObject(([pKey, slots]) => [pKey, slots.groupBy(subGroup)]);
  }, [slots, groupBy]);
}

type SlotRowProps = {
  dpSlot: DesignPackageLeftNavSlotFragment;
  selected: boolean;
  onClick: () => void;
  onActionClick?: () => void;
  variant?: "normal" | "pendingDeletion" | "pendingReview" | "rejected";
  scrollRef?: React.RefObject<HTMLDivElement> | null;
};

const slotRowVariantStyles: Record<
  NonNullable<SlotRowProps["variant"]>,
  { hoverBg: object; selectedBg: object; textColor?: object; extraRow?: object; dotColor?: string }
> = {
  normal: { hoverBg: Css.onHover.bgBlue100.$, selectedBg: Css.bgBlue50.$ },
  pendingReview: {
    hoverBg: Css.onHover.bgOrange100.$,
    selectedBg: Css.bgOrange100.$,
    textColor: Css.orange600.$,
    dotColor: Palette.Orange500,
  },
  rejected: { hoverBg: Css.onHover.bgGray200.$, selectedBg: Css.bgGray200.$ },
  pendingDeletion: {
    hoverBg: Css.onHover.bgRed100.$,
    selectedBg: Css.bgRed100.$,
    textColor: Css.red600.$,
    extraRow: Css.jcsb.$,
    dotColor: Palette.Red500,
  },
};

function SlotRow(props: SlotRowProps) {
  const { dpSlot, selected, onClick, onActionClick, variant = "normal", scrollRef } = props;
  const isPendingDeletion = variant === "pendingDeletion";
  const isPendingReview = variant === "pendingReview";
  const slotName = dpSlot.slot.name || dpSlot.slot.item.name;
  const tid = useTestIds({}, slotName);
  const { hoverBg, selectedBg, textColor, extraRow, dotColor } = slotRowVariantStyles[variant];
  return (
    <div
      ref={scrollRef}
      {...tid}
      css={{
        ...Css.df.aic.ml1.px1.pyPx(4).cursorPointer.br4.$,
        ...hoverBg,
        ...(selected ? selectedBg : {}),
        ...extraRow,
        ...textColor,
      }}
      onClick={onClick}
    >
      <div css={Css.df.aic.if(isPendingDeletion || isPendingReview).sm.gray800.$}>
        {dotColor ? (
          <div css={{ ...Css.sqPx(20).br100.fs0.$, backgroundColor: dotColor }} />
        ) : dpSlot.isFullyAssigned ? (
          <Icon icon="checkCircleFilled" color={Palette.Green500} xss={Css.fs0.$} />
        ) : dpSlot.coverage > 0 ? (
          <div css={Css.sqPx(20).bgBlue400.white.br100.df.jcc.aic.xl2.fs0.$}>-</div>
        ) : (
          <div css={Css.sqPx(20).bgGray300.br100.fs0.$} />
        )}
        <div css={Css.ml3.$}>{startCase(slotName)}</div>
      </div>
      {isPendingDeletion && (
        <div css={Css.o0.vh.when(marker, "ancestor", ":hover").o100.vv.$}>
          <IconButton compact tooltip="Restore" icon="undoCircle" onClick={() => onActionClick?.()} />
        </div>
      )}
    </div>
  );
}

function useAdhocFilterSlots(slots: DesignPackageLeftNavSlotFragment[], search: string, auditing: boolean) {
  return useMemo(
    () =>
      slots
        .filter((slot) => !auditing || !slot.isFullyAssigned)
        .filter((slot) => {
          if (!search) return true;
          const lower = search.toLowerCase();
          return [
            slot.slot.name,
            slot.slot.item.name,
            slot.slot.item.fullCode,
            slot.slot.item.costCode?.name,
            slot.slot.location.displayLocationPath,
          ]
            .compact()
            .some((str) => str.toLowerCase().includes(lower));
        }),
    [slots, search, auditing],
  );
}
